import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { makeStateKey, TransferState } from "@angular/platform-browser";
import {
    ActivatedRouteSnapshot,
    Resolve,
    Router,
    RouterStateSnapshot,
} from "@angular/router";
import { filter } from "rxjs/operators";

import { VenueService } from "./venue.service";
import { MlpHelper } from "../shared/mlpHelper";
import { PitchData } from "../shared/model/pitch";
import { VenueData } from "../shared/model/venue";
import { LinkService } from "../shared/link.service";
import { ApiEndpoints } from "../shared/apiEndpoints";
import { ConfigureService } from "../configure/configure.service";
import { DiscontinuedSportService } from "../discontinued-sport/discontinued-sport.service";
import { SplitPaymentsService } from "app/split-payments/split-payments.service";
import { SplitPaymentDetails } from "app/shared/model/splitPaymentDetails";

const PITCHDATA = makeStateKey("pitchData");
const VENUEDATA = makeStateKey("venueData");
const SPLITVENUEDATA = makeStateKey("splitVenueData")

@Injectable()
export class PitchResolver implements Resolve<any> {
    private isBrowser: boolean = isPlatformBrowser(this.platformId);

    constructor(
        private router: Router,
        private venueService: VenueService,
        private apiEndpoints: ApiEndpoints,
        private linkService: LinkService,
        private transferState: TransferState,
        private discontinuedSportService: DiscontinuedSportService,
        @Inject(PLATFORM_ID) private platformId,
        private configureService: ConfigureService,
        private splitPaymentService: SplitPaymentsService,

    ) {}

    resolve(
        activatedRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): any {
        const pitchUrlSuffix = activatedRoute.params["sportFormatPitchId"];
        const pitchId = MlpHelper.getPitchIdFromUrl(pitchUrlSuffix);

        const splitPaymentGroupId = activatedRoute.queryParams["pgid"];
        const splitPaymentPGBookerId = activatedRoute.queryParams["u"];
        this.splitPaymentService.splitPaymentPGBookerId = splitPaymentPGBookerId;

        this.configureService.paramsLoadedSubject
            .pipe(filter((data) => !!data))
            .subscribe(() => {
                this.apiEndpoints.callMlpApiPitch(pitchId).subscribe(
                    (pitchData: PitchData) => {
                        // only do any work if url matches weblink
                        if (
                            MlpHelper.isUrlEqual(
                                state.url,
                                pitchData.data.links.weblink
                            )
                        ) {
                            const venueId = pitchData.included[0].id;

                            this.apiEndpoints
                                .callMlpApiVenue(venueId)
                                .subscribe((venueData: VenueData) => {
                                    if (
                                        !this.venueService.pitchData ||
                                        (this.venueService.pitchData &&
                                            this.venueService.pitchData.data
                                                .id !== pitchId)
                                    ) {
                                        this.venueService.pitchSubject.next(
                                            pitchData
                                        );
                                    }

                                    this.venueService.pitchData = pitchData;
                                    this.venueService.pitchDataChange.emit(
                                        pitchData
                                    );

                                    this.venueService.venueData = venueData;
                                    this.venueService.venueDataChange.emit(
                                        venueData
                                    );

                                    // render canonical url and save data to pass to the client
                                    if (!this.isBrowser) {
                                        this.transferState.set(
                                            PITCHDATA,
                                            pitchData
                                        );
                                        this.transferState.set(
                                            VENUEDATA,
                                            venueData
                                        );
                                    }
                                });
                        } else {
                            this.venueService.venueDataChange.emit(null);
                        }
                    },
                    () => {
                        if(!pitchUrlSuffix) return
                        const sport = pitchUrlSuffix.split("-")[0];

                        if (
                            this.discontinuedSportService.isDiscontinuedSport(
                                sport
                            )
                        ) {
                            this.router.navigate(
                                [
                                    `${activatedRoute.params["city"]}/${sport}/404`,
                                ],
                                { skipLocationChange: true }
                            );
                        } else {
                            this.venueService.venueDataChange.emit(null);
                        }
                    }
                );

                if(splitPaymentGroupId && splitPaymentPGBookerId) {
                    this.splitPaymentService.isSplitPayment = true;
                    this.splitPaymentService
                    .getSplitPayments(splitPaymentGroupId, splitPaymentPGBookerId)
                    .subscribe((data: SplitPaymentDetails) => {
                      const venueData = data;
        
                      if (
                        !this.venueService.splitVenueData ||
                        (this.venueService.splitVenueData &&
                            this.venueService.splitVenueData.data
                                .id !== splitPaymentGroupId)
                    ) {
                        this.venueService.splitVenueSubject.next(
                            venueData
                        );
                    }
                      this.venueService.splitVenueData = venueData;
                      this.venueService.splitVenueDataChange.emit(
                          venueData
                      );
                        this.transferState.set(
                            SPLITVENUEDATA,
                            venueData
                        );
                      
                      this.splitPaymentService
                          .getCancellationPolicy(venueData.data.attributes.metadata.booking, splitPaymentPGBookerId)
                          .subscribe((data) => {
                            this.splitPaymentService.cancellationPolicy = {
                              cancellationDate: new Date(data.attributes.cancellation_date),
                              cancellationAllowed: data.attributes.cancellation_allowed
                            }
                          }, (error) => {
                            this.splitPaymentService.cancellationPolicy  = null;
                          });
                    });
                }
            });

        
        
    }
}


